import React, { useState, useEffect } from 'react';
import './NewsletterModal.css';

const NewsletterModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        captcha: ''
    });
    const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    // Generate random captcha question
    useEffect(() => {
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        // Clear previous messages
        setSubmitMessage('');

        if (!formData.name.trim()) {
            setSubmitMessage('📝 Please enter your name');
            return false;
        }

        if (!formData.email.trim()) {
            setSubmitMessage('📧 Please enter your email address');
            return false;
        }

        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setSubmitMessage('📧 Please enter a valid email address (e.g., your@email.com)');
            return false;
        }

        if (!formData.captcha.trim()) {
            setSubmitMessage('🔢 Please solve the math question');
            return false;
        }

        if (parseInt(formData.captcha) !== captchaQuestion.answer) {
            setSubmitMessage('🔢 Incorrect answer to the math question. Please try again.');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitMessage('');

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Use the newsletter service
            const { subscribeToNewsletter } = await import('../services/newsletterService');

            const result = await subscribeToNewsletter({
                name: formData.name,
                email: formData.email,
                timestamp: new Date().toISOString()
            });

            if (result.success) {
                setSubmitMessage('🎉 Thank you for subscribing! You will receive updates about our work.');
                setFormData({ name: '', email: '', captcha: '' });
                setTimeout(() => {
                    onClose();
                }, 2500);
            } else {
                // Handle specific error cases
                const errorMessage = result.error || 'Something went wrong. Please try again.';

                // If it's a configuration error, try localStorage as fallback
                if (errorMessage.includes('not configured') || errorMessage.includes('Using local storage instead')) {
                    console.log('Attempting localStorage fallback...');

                    // Try localStorage fallback
                    try {
                        const { subscribeWithLocalStorage } = await import('../services/newsletterService');
                        const fallbackResult = await subscribeWithLocalStorage({
                            name: formData.name,
                            email: formData.email,
                            timestamp: new Date().toISOString()
                        });

                        if (fallbackResult.success) {
                            setSubmitMessage('🎉 Thank you for subscribing! You will receive updates about our work.');
                            setFormData({ name: '', email: '', captcha: '' });
                            setTimeout(() => {
                                onClose();
                            }, 2500);
                            return;
                        }
                    } catch (fallbackError) {
                        console.error('Fallback attempt failed:', fallbackError);
                    }
                }

                setSubmitMessage(errorMessage);

                // Log technical details for debugging (only in development)
                if (import.meta.env.DEV) {
                    console.error('Newsletter subscription error:', result);
                }
            }
        } catch (error) {
            // Handle unexpected errors gracefully
            console.error('Unexpected subscription error:', error);
            setSubmitMessage('⚠️ Sorry, there was an unexpected error. Please try again later or contact support.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="newsletter-modal-overlay" onClick={onClose}>
            <div className="newsletter-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>
                    ×
                </button>

                <div className="modal-header">
                    <h2>SUBSCRIBE AND STAY CONNECTED WITH US</h2>
                    <p>
                        Be the first to know about our moving stories, new projects
                        and opportunities. Join our community of changemakers today!
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="newsletter-form">
                    <div className="form-group">
                        <label htmlFor="name">Name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter your full name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter your email address"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="captcha">
                            What is {captchaQuestion.num1} + {captchaQuestion.num2}?
                        </label>
                        <input
                            type="number"
                            id="captcha"
                            name="captcha"
                            value={formData.captcha}
                            onChange={handleInputChange}
                            placeholder="Enter sum"
                            required
                        />
                    </div>

                    {submitMessage && (
                        <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
                            {submitMessage}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default NewsletterModal; 